import React from "react";
import {
  Box,
  Container,
  SimpleGrid,
  Heading,
  Text,
  Image,
  VStack,
  Badge,
  Button,
  HStack,
  Icon,
  useColorModeValue,
  Grid,
  Divider,
  IconButton,
  Flex,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Link as RouterLink } from "react-router-dom";
import { 
  FiShoppingBag, 
  FiStar, 
  FiHeart,
  FiDroplet,
  FiClock,
  FiArrowRight,
} from "react-icons/fi";
import { 
  FaMagic, 
  FaLeaf,
  FaGem,
  FaRegClock,
  FaSprayCan,
  FaCertificate,
  FaShippingFast,
  FaTree,
  FaWind,
  FaCookie,
} from "react-icons/fa";
import { categories } from "../data/categories";
import { products } from "../data/products";
import type { Product } from "../types";

const MotionBox = motion(Box);

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.5,
    },
  },
};

const Categories: React.FC = () => {
  const bgColor = useColorModeValue("white", "gray.800");
  const textColor = useColorModeValue("gray.600", "gray.300");

  // Get category icons
  const getCategoryIcon = (categoryId: string) => {
    switch (categoryId) {
      case "floral":
        return FaLeaf;
      case "oriental":
        return FaMagic;
      case "woody":
        return FaTree;
      case "fresh":
        return FaWind;
      case "gourmand":
        return FaCookie;
      case "aromatic":
        return FaSprayCan;
      default:
        return FaSprayCan;
    }
  };

  // Get category product count
  const getCategoryProductCount = (categoryId: string) => {
    return products.filter((product: Product) => product.category === categoryId).length;
  };

  return (
    <Box py={8}>
      <Container maxW="1400px">
        <VStack spacing={8} align="stretch">
          {/* Categories Header */}
          <Box textAlign="center" py={8}>
            <Heading size="2xl" color="purple.700" mb={4}>
              Perfume Collections
            </Heading>
            <Text fontSize="xl" color="purple.500" mb={6}>
              Discover our curated selection of luxury fragrances
            </Text>
            <Text fontSize="lg" color="gray.600">
              Explore different fragrance families and find your signature scent
            </Text>
          </Box>

          <Divider />

          {/* Categories Grid */}
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {categories.map((category) => (
              <MotionBox
                key={category.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Box
                  as={RouterLink}
                  to={`/category/${category.id}`}
                  cursor="pointer"
                  _hover={{ textDecoration: "none" }}
                >
                  <Box
                    borderWidth="1px"
                    borderRadius="xl"
                    overflow="hidden"
                    bg={bgColor}
                    shadow="md"
                    transition="all 0.3s"
                    _hover={{
                      shadow: "xl",
                      transform: "translateY(-4px)",
                    }}
                    height="100%"
                  >
                    <Box 
                      position="relative" 
                      height="280px" 
                      bg="purple.50"
                      p={8}
                    >
                      <Flex
                        direction="column"
                        align="center"
                        justify="center"
                        height="100%"
                      >
                        <Icon
                          as={getCategoryIcon(category.id)}
                          w={20}
                          h={20}
                          color="purple.500"
                          mb={6}
                        />
                        <Heading size="lg" color="purple.700" mb={3}>
                          {category.name}
                        </Heading>
                        <Text 
                          color="gray.600" 
                          textAlign="center" 
                          mb={6}
                          fontSize="md"
                          lineHeight="1.6"
                          px={4}
                        >
                          {category.description}
                        </Text>
                        <HStack spacing={2} mt="auto">
                          <Text color="purple.500" fontWeight="bold" fontSize="md">
                            {getCategoryProductCount(category.id)} Fragrances
                          </Text>
                          <Icon as={FiArrowRight} color="purple.500" />
                        </HStack>
                      </Flex>
                    </Box>
                  </Box>
                </Box>
              </MotionBox>
            ))}
          </SimpleGrid>

          {/* Features Section */}
          <Box mt={16} py={12} bg="purple.50" borderRadius="xl">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} px={8}>
              <Flex direction="column" align="center" textAlign="center" p={6}>
                <Icon as={FaCertificate} w={10} h={10} color="purple.500" mb={4} />
                <Text fontWeight="bold" fontSize="lg" color="purple.700" mb={2}>
                  Authentic Products
                </Text>
                <Text color="gray.600" fontSize="md">
                  100% genuine luxury fragrances
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" p={6}>
                <Icon as={FiClock} w={10} h={10} color="purple.500" mb={4} />
                <Text fontWeight="bold" fontSize="lg" color="purple.700" mb={2}>
                  Long Lasting
                </Text>
                <Text color="gray.600" fontSize="md">
                  Premium quality fragrances
                </Text>
              </Flex>
              <Flex direction="column" align="center" textAlign="center" p={6}>
                <Icon as={FaShippingFast} w={10} h={10} color="purple.500" mb={4} />
                <Text fontWeight="bold" fontSize="lg" color="purple.700" mb={2}>
                  Fast Delivery
                </Text>
                <Text color="gray.600" fontSize="md">
                  Quick and reliable shipping
                </Text>
              </Flex>
            </SimpleGrid>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
};

export default Categories;
